import React, { useEffect, useRef } from 'react';
import { IconButton } from '../Button';
import './Dialog.css';
// @ts-ignore
import closeIcon from '../../image-assets/icon.close.svg';
import { useDialog } from '../../context/DialogContext';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  headerActions?: React.ReactNode;
  showCloseButton?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

const Dialog: React.FC<DialogProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  actions,
  headerActions,
  showCloseButton = true,
  className,
  size = 'medium'
}) => {
  const [isAnimating, setIsAnimating] = React.useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const { onDialogOpen, onDialogClose } = useDialog();

  // Handle dialog open/close effects
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        setIsAnimating(true);
        onDialogOpen();
      }, 10);
      
      // Add event listeners and set body style
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      setIsAnimating(false);
      onDialogClose();
      
      // Remove event listeners and reset body style
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Don't render anything if the dialog is not open
  if (!isOpen) return null;

  return (
    <div 
      className={`dialog-overlay ${isAnimating ? 'visible' : ''}`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`dialog-container dialog-size-${size} ${isAnimating ? 'visible' : ''} ${className || ''}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
      >
        <div className="dialog-header">
          <h2 id="dialog-title" className="dialog-title">{title}</h2>
          <div className="dialog-header-actions">
            {headerActions}
            {showCloseButton && (
              <IconButton 
                variant="text" 
                size="small"
                onClick={onClose}
                aria-label="Close dialog"
                icon={closeIcon}
              />
            )}
          </div>
        </div>
        <div className="dialog-content">
          {children}
        </div>
        {actions && (
          <div className="dialog-actions">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dialog; 